import React, { useState, useEffect } from "react";
import { Link, useNavigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

import {
  LogOut,
  Users,
  Shapes,
  Settings as SettingsIcon,
  LayoutDashboard,
  Shield,
  Sparkles,
  Star,
  Menu,
  X,
  ChevronRight,
  BookOpen,
} from "lucide-react";

const menuItems = [
  {
    path: "/admin",
    end: true,
    label: "Categories",
    icon: Shapes,
    description: "Manage topics and subtopics",
  },
  {
    path: "/admin/vocabulary",
    end: false,
    label: "Vocabulary",
    icon: BookOpen,
    description: "Manage emotion words (sad, anxious, happy, angry, neutral)",
  },
  {
    path: "/admin/users",
    end: false,
    label: "Users",
    icon: Users,
    description: "Manage user accounts",
  },
  {
    path: "/admin/subscriptions",
    end: false,
    label: "Subscriptions",
    icon: Star,
    description: "Manage pricing plans",
  },
  {
    path: "/admin/settings",
    end: false,
    label: "Settings",
    icon: SettingsIcon,
    description: "System configuration",
  },
  {
    path: "/admin/indexing",
    end: false,
    label: "Indexing",
    icon: Sparkles,
    description: "Indexing sentences",
  },
];

const AdminPanel: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useAuth();

  const activeItem =
    menuItems
      .slice()
      .reverse()
      .find((item) =>
        item.end ? pathname === item.path : pathname.startsWith(item.path)
      ) ?? menuItems[0];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  if (user?.roleId !== 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 animate-in fade-in zoom-in duration-300">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Access Denied
            </h1>
            <p className="text-gray-600 mb-6">
              Admin access is required to view this page.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-xl font-medium hover:bg-gray-800 transition-all transform hover:scale-105"
            >
              Back to Chat
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const initials =
    (user?.username || "A")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w: string) => w[0]?.toUpperCase())
      .join("") || "A";

  const SidebarNav = ({ onNavigate }: { onNavigate?: () => void }) => (
    <nav className="flex-1 overflow-y-auto hide-scrollbar p-4 space-y-2">
      {menuItems.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              `w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                isActive
                  ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon
                  className={`w-5 h-5 transition-transform ${isActive ? "scale-110" : "group-hover:scale-110"}`}
                />
                <div className="flex-1 text-left">
                  <div className={`font-medium ${isActive ? "text-white" : "text-gray-700"}`}>
                    {item.label}
                  </div>
                  <div className={`text-xs ${isActive ? "text-white/80" : "text-gray-500"}`}>
                    {item.description}
                  </div>
                </div>
                {isActive && <ChevronRight className="w-4 h-4" />}
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
              <LayoutDashboard className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                Admin Dashboard
              </h1>
              <p className="text-xs text-gray-500">Control Panel</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 transition"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="flex h-screen overflow-hidden">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:flex lg:w-80 flex-col bg-white border-r border-gray-200 shadow-sm h-screen">
          {/* Sticky Header */}
          <div className="flex-shrink-0 p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                <LayoutDashboard className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Admin Dashboard
                </h1>
                <p className="text-xs text-gray-500">Complete control panel</p>
              </div>
            </div>
          </div>

          {/* Scrollable Navigation Menu */}
          <SidebarNav />

          {/* Sticky Footer - Logout */}
          <div className="flex-shrink-0 p-6 border-t border-gray-200 bg-white">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-all duration-200 group"
            >
              <LogOut className="w-5 h-5 group-hover:scale-110 transition-transform" />
              <div className="flex-1 text-left">
                <div className="font-medium">Logout</div>
                <div className="text-xs text-gray-500">Sign out from account</div>
              </div>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          {/* Desktop Header */}
          <div className="hidden lg:block sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200">
            <div className="px-8 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                    {activeItem.label}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {activeItem.description}
                  </p>
                </div>
                <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-gradient-to-r from-gray-50 to-white border border-gray-200 shadow-sm">
                  <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-gray-800 to-gray-900 text-white flex items-center justify-center text-sm font-semibold shadow-md">
                    {initials}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 leading-tight">
                      {user?.username || "Admin"}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Administrator
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 lg:p-8">
            {/* Mobile Sidebar Overlay */}
            {isMobile && sidebarOpen && (
              <>
                <div
                  className="fixed inset-0 bg-black/50 z-50"
                  onClick={() => setSidebarOpen(false)}
                />
                <aside className="fixed left-0 top-0 bottom-0 w-80 bg-white z-50 shadow-xl animate-in slide-in-from-left duration-300 flex flex-col">
                  {/* Sticky Header */}
                  <div className="flex-shrink-0 p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
                          <LayoutDashboard className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h1 className="text-lg font-bold">Admin Dashboard</h1>
                          <p className="text-xs text-gray-500">Control Panel</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setSidebarOpen(false)}
                        className="p-2 rounded-lg hover:bg-gray-100"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Scrollable Navigation Menu */}
                  <SidebarNav onNavigate={() => setSidebarOpen(false)} />

                  {/* Sticky Footer - Logout */}
                  <div className="flex-shrink-0 p-6 border-t border-gray-200 bg-white">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-all"
                    >
                      <LogOut className="w-5 h-5" />
                      <div className="flex-1 text-left">
                        <div className="font-medium">Logout</div>
                        <div className="text-xs text-gray-500">Sign out</div>
                      </div>
                    </button>
                  </div>
                </aside>
              </>
            )}

            {/* Content Area - rendered by child routes */}
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                <Outlet />
              </div>
            </div>

            {/* Footer Hint */}
            <div className="mt-8 text-center">
              <div className="inline-block bg-gradient-to-r from-gray-50 to-gray-100 rounded-full px-6 py-2 shadow-sm">
                <p className="text-xs text-gray-500 flex items-center gap-2">
                  <Sparkles className="w-3 h-3" />
                  Tip: Keep names short, use consistent icons, and avoid too
                  many active categories
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminPanel;
